import { useState, useMemo } from "react";
import { velilerData, Veli } from "@/mocks/veliler";
import VeliDetayPanel from "./VeliDetayPanel";

type FilterType = "tumu" | "aktif" | "yeni" | "referansVeren" | "referansVermeyen" | "odemeYaklaşan" | "taahhutBitmek" | "pasif";

const FILTERS: { key: FilterType; label: string }[] = [
  { key: "tumu", label: "Tüm Kayıtlar" },
  { key: "aktif", label: "Aktif Öğrenciler" },
  { key: "yeni", label: "Yeni Kayıtlar" },
  { key: "referansVeren", label: "Referans Verenler" },
  { key: "referansVermeyen", label: "Referans Vermeyenler" },
  { key: "odemeYaklaşan", label: "Ödeme Yaklaşan" },
  { key: "taahhutBitmek", label: "Taahhüdü Bitmek Üzere" },
  { key: "pasif", label: "Pasif Öğrenciler" },
];

function StatusBadge({ veli }: { veli: Veli }) {
  const today = new Date();
  const taahhutBitis = new Date(veli.taahhutBitis);
  const taahButDiff = Math.ceil((taahhutBitis.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  const odemeYaklasiyor = Math.abs(veli.odemeGunu - today.getDate()) <= 2;

  if (veli.ogrenciDurumu === "Pasif") {
    return <span className="px-2.5 py-0.5 bg-gray-100 text-gray-500 rounded-full text-[11px] font-medium whitespace-nowrap">Pasif</span>;
  }
  if (veli.ogrenciDurumu === "Yeni") {
    return <span className="px-2.5 py-0.5 bg-sky-100 text-sky-600 rounded-full text-[11px] font-medium whitespace-nowrap">Yeni Kayıt</span>;
  }
  if (taahButDiff <= 30 && taahButDiff >= 0) {
    return <span className="px-2.5 py-0.5 bg-orange-100 text-orange-600 rounded-full text-[11px] font-medium whitespace-nowrap">Taahhüt Yaklaşıyor</span>;
  }
  if (odemeYaklasiyor) {
    return <span className="px-2.5 py-0.5 bg-amber-100 text-amber-600 rounded-full text-[11px] font-medium whitespace-nowrap">Ödeme Yaklaşıyor</span>;
  }
  return <span className="px-2.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-[11px] font-medium whitespace-nowrap">Aktif</span>;
}

function ReferansBadge({ value }: { value: string }) {
  if (value === "Verdi") return <span className="px-2.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-[11px] font-medium whitespace-nowrap">Referans Verdi</span>;
  if (value === "Vermedi") return <span className="px-2.5 py-0.5 bg-red-100 text-red-600 rounded-full text-[11px] font-medium whitespace-nowrap">Referans Vermedi</span>;
  return <span className="px-2.5 py-0.5 bg-amber-100 text-amber-600 rounded-full text-[11px] font-medium whitespace-nowrap">Bekleniyor</span>;
}

interface ActionMenuProps {
  veli: Veli;
  onDetay: () => void;
  onClose: () => void;
}

function ActionMenu({ onDetay, onClose }: ActionMenuProps) {
  const items = [
    { icon: "ri-eye-line", label: "Detay", action: onDetay },
    { icon: "ri-mail-send-line", label: "Mesaj Gönder", action: onClose, green: true },
    { icon: "ri-edit-line", label: "Düzenle", action: onClose },
    { icon: "ri-sticky-note-line", label: "Not Ekle", action: onClose },
  ];

  return (
    <div
      className="absolute right-0 top-8 w-44 bg-white rounded-xl shadow-xl border border-gray-100 py-1.5 z-20"
      onClick={(e) => e.stopPropagation()}
    >
      {items.map((item, i) => (
        <button
          key={i}
          onClick={() => { item.action(); onClose(); }}
          className={`w-full flex items-center gap-2.5 px-4 py-2 text-sm hover:bg-gray-50 cursor-pointer transition-colors text-left whitespace-nowrap
            ${item.green ? "text-[#25D366]" : "text-gray-700"}
          `}
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className={`${item.icon} text-sm`}></i>
          </div>
          {item.label}
        </button>
      ))}
    </div>
  );
}

export default function VelilerTable() {
  const [activeFilter, setActiveFilter] = useState<FilterType>("tumu");
  const [search, setSearch] = useState("");
  const [openMenu, setOpenMenu] = useState<number | null>(null);
  const [detayVeli, setDetayVeli] = useState<Veli | null>(null);

  const today = new Date();

  const filtered = useMemo(() => {
    let data = velilerData;
    if (activeFilter === "aktif") data = data.filter((v) => v.ogrenciDurumu === "Aktif");
    else if (activeFilter === "yeni") data = data.filter((v) => v.ogrenciDurumu === "Yeni");
    else if (activeFilter === "referansVeren") data = data.filter((v) => v.referansDurumu === "Verdi");
    else if (activeFilter === "referansVermeyen") data = data.filter((v) => v.referansDurumu === "Vermedi");
    else if (activeFilter === "pasif") data = data.filter((v) => v.ogrenciDurumu === "Pasif");
    else if (activeFilter === "odemeYaklaşan") {
      data = data.filter((v) => Math.abs(v.odemeGunu - today.getDate()) <= 3);
    } else if (activeFilter === "taahhutBitmek") {
      data = data.filter((v) => {
        const bitis = new Date(v.taahhutBitis);
        const diff = Math.ceil((bitis.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        return diff <= 30 && diff >= 0;
      });
    }

    if (search) {
      const q = search.toLowerCase();
      data = data.filter(
        (v) =>
          v.veliAdi.toLowerCase().includes(q) ||
          v.ogrenciAdi.toLowerCase().includes(q) ||
          v.telefon.includes(q) ||
          v.seviye.toLowerCase().includes(q)
      );
    }
    return data;
  }, [activeFilter, search]);

  return (
    <>
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        {/* Filter tabs */}
        <div className="border-b border-gray-100 overflow-x-auto">
          <div className="flex min-w-max px-4 pt-4 gap-1">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setActiveFilter(f.key)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap mb-3
                  ${activeFilter === f.key
                    ? "bg-[#0B1120] text-white"
                    : "text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                  }
                `}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Search + Actions */}
        <div className="flex flex-col sm:flex-row gap-3 p-4 border-b border-gray-100">
          <div className="flex-1 relative">
            <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
              <i className="ri-search-line text-sm"></i>
            </div>
            <input
              type="text"
              placeholder="Veli adı, öğrenci adı veya telefon ara..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#25D366]/30 focus:border-[#25D366] bg-gray-50"
            />
          </div>
          <div className="flex gap-2">
            <button className="px-3 py-2.5 text-xs border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap flex items-center gap-1.5">
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-filter-3-line text-xs"></i>
              </div>
              Filtrele
            </button>
            <button className="px-3 py-2.5 text-xs bg-[#25D366] text-white rounded-xl hover:bg-[#1da851] cursor-pointer whitespace-nowrap flex items-center gap-1.5 transition-colors">
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-whatsapp-line text-xs"></i>
              </div>
              Toplu Gönder
            </button>
          </div>
        </div>

        {/* Count */}
        <div className="px-4 py-2 bg-gray-50/50 text-xs text-gray-500">
          <strong className="text-gray-700">{filtered.length}</strong> kayıt listeleniyor
        </div>

        {/* Table */}
        <div className="overflow-x-auto" onClick={() => setOpenMenu(null)}>
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/30">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 whitespace-nowrap">Veli Adı</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 whitespace-nowrap">Öğrenci Adı</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 whitespace-nowrap">Telefon</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 whitespace-nowrap">Seviye</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 whitespace-nowrap">Ödeme Günü</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 whitespace-nowrap">Taahhüt Bitiş</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 whitespace-nowrap">Referans</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 whitespace-nowrap">Durum</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 whitespace-nowrap">Son Mesaj</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 whitespace-nowrap">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map((v) => (
                <tr
                  key={v.id}
                  className="hover:bg-gray-50/60 transition-colors cursor-pointer"
                  onClick={() => setDetayVeli(v)}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-[#0B1120] flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-[10px] font-bold">
                          {v.veliAdi.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                        </span>
                      </div>
                      <span className="text-sm font-medium text-gray-800 whitespace-nowrap">{v.veliAdi}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{v.ogrenciAdi}</td>
                  <td className="px-4 py-3">
                    <a
                      href={`https://wa.me/9${v.telefon.replace(/\s/g, "")}`}
                      className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-[#25D366] transition-colors whitespace-nowrap"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-whatsapp-line text-sm"></i>
                      </div>
                      {v.telefon}
                    </a>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{v.seviye}</td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-700 whitespace-nowrap">{v.odemeGunu}. gün</td>
                  <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{v.taahhutBitis}</td>
                  <td className="px-4 py-3"><ReferansBadge value={v.referansDurumu} /></td>
                  <td className="px-4 py-3"><StatusBadge veli={v} /></td>
                  <td className="px-4 py-3 text-xs text-gray-400 max-w-[160px] truncate">{v.sonMesaj}</td>
                  <td className="px-4 py-3">
                    <div className="relative" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenMenu(openMenu === v.id ? null : v.id);
                        }}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 cursor-pointer transition-colors"
                      >
                        <i className="ri-more-2-fill text-base"></i>
                      </button>
                      {openMenu === v.id && (
                        <ActionMenu
                          veli={v}
                          onDetay={() => setDetayVeli(v)}
                          onClose={() => setOpenMenu(null)}
                        />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filtered.length === 0 && (
            <div className="text-center py-16">
              <div className="w-12 h-12 flex items-center justify-center mx-auto text-gray-300 mb-3">
                <i className="ri-search-line text-3xl"></i>
              </div>
              <p className="text-sm text-gray-500">Sonuç bulunamadı</p>
            </div>
          )}
        </div>
      </div>

      {/* Detay Panel */}
      {detayVeli && (
        <VeliDetayPanel veli={detayVeli} onClose={() => setDetayVeli(null)} />
      )}
    </>
  );
}